import React, { useEffect, useMemo } from 'react';

import { ContentType } from 'application-types';
import { useGetAuthToken } from 'hooks/useGetAuthToken';
import { useIsMounted } from 'hooks/useIsMounted';
import { useUserDataContext } from 'lane-shared/hooks';
import { getPrimaryEmail } from 'lane-shared/helpers/user';
import { authorizationHeader } from 'lane-shared/helpers/formatters';
import { useTenantInfo } from 'lane-shared/domains/visitorManagement/hooks/useTenantInfo';
import { convertToUUID } from 'uuid-encoding';
import {
  VantifyWorkOrderMain,
  Header,
  AccessDeniedCard,
} from 'integrations-vantify-web-react';
import {
  VantifyContextProvider,
  useVantifyContext,
} from 'integrations-vantify-context-providers';
import styles from './Welcome.module.scss';

type Props = {
  content: ContentType;
};

function VantifyContent({ content }: Props) {
  const getAuthToken = useGetAuthToken();
  const { setAuth, auth, setUser, setContent } = useVantifyContext();
  const isMounted = useIsMounted();
  const { user } = useUserDataContext();
  const channelId = content?.channel?._id;
  const tenantInfo = useTenantInfo(channelId);

  useEffect(() => {
    if (!content || !isMounted()) {
      return;
    }

    setContent(content);

    async function setupAuth() {
      const currentChannelId = auth?.channelId
        ? convertToUUID(auth.channelId)
        : '';
      const newChannelId = content.channel?._id
        ? convertToUUID(content.channel._id)
        : '';

      if (!auth || currentChannelId !== newChannelId) {
        const authTokenResult = await getAuthToken();

        if (isMounted()) {
          setAuth({
            channelId: newChannelId,
            token: authorizationHeader(authTokenResult),
          });
        }
      }
    }

    setupAuth();
  }, [auth, content, getAuthToken, setAuth, setContent, isMounted]);

  useEffect(() => {
    if (user) {
      const email = user.logins ? getPrimaryEmail(user.logins) : null;
      const name = user.name || null;

      setUser({ email, name });
    }
  }, [user, setUser]);

  const isUserTenantInMappings = useMemo(() => {
    const tenantMappings = content?.integration?.settings?.tenantMappings;
    const userTenantId = tenantInfo?.id;

    if (
      !tenantMappings ||
      !Array.isArray(tenantMappings) ||
      tenantMappings.length === 0 ||
      !userTenantId
    ) {
      return false;
    }

    return tenantMappings.some(
      mapping => mapping?.vtsTenantId === userTenantId
    );
  }, [tenantInfo?.id, content?.integration?.settings?.tenantMappings]);

  const shouldShowAccessDenied = useMemo(() => {
    return !auth || !content?.integration || !isUserTenantInMappings;
  }, [auth, content, isUserTenantInMappings]);

  if (shouldShowAccessDenied) {
    return (
      <div className={styles.container}>
        <Header disabled />
        <AccessDeniedCard />
      </div>
    );
  }

  return <VantifyWorkOrderMain />;
}

export default function Vantify({ content }: Props) {
  return (
    <VantifyContextProvider>
      <VantifyContent content={content} />
    </VantifyContextProvider>
  );
}
